import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { operatesAsTrader } from 'app/core/user/domain-roles';
import { KioskService } from 'app/core/services/kiosk.service';

/**
 * Kiosk pages stay behind the setup question until this device is
 * explicitly put in kiosk mode.
 */
export const kioskModeEnabledGuard: CanActivateFn = () => {
    const kiosk = inject(KioskService);
    const router = inject(Router);
    if (kiosk.isKiosk) {
        return true;
    }
    return router.parseUrl('/kiosk-setup');
};

/**
 * Restricts in-house kiosk tools (e.g. `/kiosk/packages`) to staff who run
 * shipping/receiving: Admin or Trader only.
 *
 * The kiosk's landing surface (`/kiosk/pricing`) is customer-facing and may be
 * driven by a non-staff (e.g. Sales) session, so anyone who isn't Admin/Trader
 * is bounced back to pricing rather than shown order data.
 */
export const kioskAdminTraderGuard: CanActivateFn = () => {
    const router = inject(Router);

    try {
        const raw = localStorage.getItem('userData');
        if (raw && raw !== 'null' && raw !== 'undefined') {
            const user = JSON.parse(raw);
            if (user?.role === 'Admin' || operatesAsTrader(user)) {
                return true;
            }
        }
    } catch {
        // Fall through to redirect on any parse error.
    }

    return router.parseUrl('/kiosk/pricing');
};

/**
 * Access guard for the kiosk Schedule station (`/kiosk/schedule`).
 *
 * Mirrors the desktop Printer View / Schedule access (Admin, Production,
 * Trader, Printer) since a Printer panel is a single-purpose Schedule station.
 * Anyone else is bounced back to the customer-facing pricing surface.
 */
export const kioskScheduleGuard: CanActivateFn = () => {
    const router = inject(Router);

    try {
        const raw = localStorage.getItem('userData');
        if (raw && raw !== 'null' && raw !== 'undefined') {
            const user = JSON.parse(raw);
            const role = user?.role;
            if (
                role === 'Admin' ||
                role === 'Production' ||
                role === 'Printer' ||
                operatesAsTrader(user)
            ) {
                return true;
            }
        }
    } catch {
        // Fall through to redirect on any parse error.
    }

    return router.parseUrl('/kiosk/pricing');
};

/** The cart kiosk is limited to deployment staff and assigned press operators. */
export const kioskAdminGuard: CanActivateFn = () => {
    const router = inject(Router);

    try {
        const raw = localStorage.getItem('userData');
        if (raw && raw !== 'null' && raw !== 'undefined') {
            const role = JSON.parse(raw)?.role;
            if (role === 'Admin' || role === 'Production' || role === 'Printer') {
                return true;
            }
        }
    } catch {
        // Fall through to redirect on any parse error.
    }

    return router.parseUrl('/kiosk/pricing');
};
